import { useCookies } from "react-cookie";
import {
  RETURNING_USERS_POST_ELIGIBILITY_COOKIE_NAME,
  RETURNING_USERS_PRE_ELIGIBILITY_COOKIE_NAME,
  RETURNING_USERS_NOTIFICATION_CLOSED_COOKIE_EXPIRATION,
  RETURNING_USERS_NOTIFICATION_CLOSED_COOKIE_NAME,
} from "config/cookies";
import { extractDomain, getExpDateByDays } from "utils/cookiesUtils";
import { useDisclosure } from "@chakra-ui/react";
import { trackUAEvent, trackCustomEvent } from "utils/tracking";
import { useEffect } from "react";
import config from "config/config";
import { useGoogleOptimizeExperimentId } from "contexts/DataContext/hooks";
import { useGoogleOptimize } from "hooks/useGoogleOptimize";
import type { LanguageCode } from "config/languages";
import { useLanguage } from "contexts/LocalizationContext";

const RETURNING_USERS_VIEW_EVENT_UA = ["viewed", "ReturningUsers", "Notification"] as const;
const RETURNING_USERS_VIEW_EVENT = { event: "returning_users", label: "viewed", placement: "notification" } as const;
const EXPERIMENT_NAME = "returning-user-phase-2.1";

const getFunnelUrl = (postEligibilityCookie: string | undefined, optimizeVariant: number, language: LanguageCode) => {
  switch (optimizeVariant) {
    case 0:
      return null;
    case 1:
      return `${config.funnelUrl}/claims/new?restore-pre-eligibility-session=true&lang=${language}`;
    default:
      if (postEligibilityCookie) return `${config.funnelUrl}/claim_enquiries/${postEligibilityCookie}?lang=${language}`;
      return null;
  }
};

const useReturningUsersNotification = () => {
  const experimentId = useGoogleOptimizeExperimentId(EXPERIMENT_NAME, [{ language: "en-int" }]);
  const variant = useGoogleOptimize(experimentId);
  const language = useLanguage();

  const [cookies, setCookie] = useCookies([
    RETURNING_USERS_PRE_ELIGIBILITY_COOKIE_NAME,
    RETURNING_USERS_POST_ELIGIBILITY_COOKIE_NAME,
    RETURNING_USERS_NOTIFICATION_CLOSED_COOKIE_NAME,
  ]);

  const funnelUrl = getFunnelUrl(cookies[RETURNING_USERS_POST_ELIGIBILITY_COOKIE_NAME], Number(variant), language);

  const hasFunnelCookie =
    cookies[RETURNING_USERS_PRE_ELIGIBILITY_COOKIE_NAME] || cookies[RETURNING_USERS_POST_ELIGIBILITY_COOKIE_NAME];
  const notificationClosedCookie = cookies[RETURNING_USERS_NOTIFICATION_CLOSED_COOKIE_NAME];
  const shouldBeOpen = funnelUrl && hasFunnelCookie && !notificationClosedCookie;

  const { isOpen, onClose: onDisclosureClose, onOpen: onDisclosureOpen } = useDisclosure({ defaultIsOpen: false });

  const onClose = () => {
    onDisclosureClose();
    setCookie(RETURNING_USERS_NOTIFICATION_CLOSED_COOKIE_NAME, "true", {
      domain: extractDomain(),
      path: "/",
      expires: getExpDateByDays(RETURNING_USERS_NOTIFICATION_CLOSED_COOKIE_EXPIRATION),
    });
  };

  useEffect(() => {
    onDisclosureClose();
    if (shouldBeOpen) {
      onDisclosureOpen();
      trackUAEvent(RETURNING_USERS_VIEW_EVENT_UA);
      trackCustomEvent(RETURNING_USERS_VIEW_EVENT);
    }
  }, [shouldBeOpen, onDisclosureOpen, onDisclosureClose]);

  return { isOpen, onClose, funnelUrl };
};

export default useReturningUsersNotification;
